import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Input, Select, Space, Switch, Tag, Typography, notification } from 'antd';
import { CalendarOutlined, ClearOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../../services/http.services';
import {
  API_ALLAIRPORT,
  API_CREATE_USAGE_RECORD,
  API_DELETE_USAGE_RECORD,
  API_GET_DATA,
  API_UPDATE_USAGE_RECORD,
  API_USAGE_RECORDS,
} from '../../api/api';
import EditableGridTable from '../../customs/EditableGridTable';
import MismatchGuideAccordion from '../common/MismatchGuideAccordion';
import CustomButton from '../../customs/CustomButton';
import { formatDisplayDate, formatIndianStyle, getTimestamp } from '../../helper/arrayFormatter';
import { exportRowsToExcel } from '../../utils/dashboard';
import {
  airlineColumnKey,
  buildUsagePayload,
  buildUsageSheetRow,
  calcAirlineSum,
  calcDyDiff,
  hasUsageRowChanged,
} from '../../utils/usageSheet';


const { Title, Text } = Typography;
const html2pdf = require('html2pdf.js');


const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const SUMMARY_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 14,
  marginBottom: 20,
};

const SUMMARY_CARD_STYLE = {
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: '14px 16px',
  background: '#fff',
  boxShadow: '0 4px 10px rgba(15, 23, 42, 0.04)',
};

const ANALYTICS_PANEL_STYLE = {
  marginBottom: 20,
  padding: 15,
  border: '1px solid #d9d9d9',
  borderRadius: 14,
  background: '#fff',
};


const parseUserAirports = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};


const formatMetric = (value, digits = null) => {
  const numericValue = Number(value || 0);
  if (digits === null) {
    return formatIndianStyle(numericValue);
  }
  return formatIndianStyle(numericValue.toFixed(digits));
};

const formatPercentValue = (value, digits = 1) => `${Number(value || 0).toFixed(digits)}%`;

const renderMetricPill = (value, options = {}) => {
  const { highlight = false, emphasize = false, color = '#D70040' } = options;

  return (
    <p
      style={{
        position: 'relative',
        backgroundColor: highlight ? color : '',
        marginBottom: 0,
      }}
      className={`text-right ${
        highlight
          ? 'text-white font-bold rounded-sm px-1'
          : emphasize
            ? 'font-semibold text-white bg-[#3B82F6] px-2 rounded-sm'
            : 'font-normal'
      }`}
    >
      {formatMetric(value)}
    </p>
  );
};


const hasNegativeTouchpoint = (row) =>
  [
    Number(row.dy_pax || 0),
    Number(row.dy_pax_entry_app || 0),
    Number(row.dy_pax_entry_otdy_kiosks || 0),
    Number(row.dy_pax_pesc || 0),
    Number(row.dy_pax_boarding || 0),
  ].some((value) => value < 0);

const normalizeCityName = (value) => String(value || '').replace(/\s*\([A-Z]{3,4}\)\s*$/, '').trim();


const hasEntryMismatch = (row) => calcDyDiff(row) !== 0 || hasNegativeTouchpoint(row);


const hasAirlineMismatch = (row, airlineNames) => Number(row.dy_pax || 0) !== calcAirlineSum(row, airlineNames);


const resolveMonthWindow = (yearValue, monthValue) => {
  const normalizedMonth = String(monthValue || '').trim();
  const normalizedYear = String(yearValue || '').trim();
  if (!normalizedMonth || !normalizedYear) {
    return null;
  }

  const monthIndex = MONTH_INDEX[normalizedMonth.slice(0, 3).toLowerCase()];
  if (monthIndex === undefined) {
    return null;
  }

  let targetYear = Number(normalizedYear);
  if (!targetYear) {
    const parts = normalizedMonth.split('-');
    if (parts.length > 1) {
      const parsedYear = Number(parts[1]);
      if (parsedYear) {
        targetYear = parsedYear < 100 ? 2000 + parsedYear : parsedYear;
      }
    }
  }

  if (!targetYear) {
    return null;
  }

  const start = dayjs(new Date(targetYear, monthIndex, 1));
  if (!start.isValid()) {
    return null;
  }

  const currentMonth = dayjs();
  const fullMonthEnd = start.endOf('month');
  const end = start.isSame(currentMonth, 'month') ? dayjs().endOf('day') : fullMonthEnd;
  return { start, end };
};


const buildDraftUsageRow = ({ airportCode, flightDate, airlineNames }) => ({
  id: null,
  airport_code: airportCode || '',
  flight_date: flightDate,
  total_dep_pax: 0,
  dy_pax: 0,
  dy_pax_percentage: 0,
  dy_pax_entry_app: 0,
  dy_pax_entry_otdy_kiosks: 0,
  dy_pax_pesc: 0,
  dy_pax_boarding: 0,
  remarks: '',
  ...Object.fromEntries((airlineNames || []).map((name) => [airlineColumnKey(name), 0])),
});


const buildContinuationDates = (latestFilledDate) => {
  if (!latestFilledDate) {
    return [];
  }

  const latestDate = dayjs(latestFilledDate).startOf('day');
  if (!latestDate.isValid()) {
    return [];
  }

  const today = dayjs().startOf('day');
  if (!latestDate.isBefore(today, 'day')) {
    return [];
  }

  const dates = [];
  let cursor = latestDate.add(1, 'day');
  while (cursor.isBefore(today) || cursor.isSame(today, 'day')) {
    dates.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }
  return dates;
};


const buildMissingDatesInRange = (startDateValue, endDateValue, existingDateKeys = []) => {
  if (!startDateValue || !endDateValue) {
    return [];
  }

  const startDate = dayjs(startDateValue).startOf('day');
  const endDate = dayjs(endDateValue).startOf('day');
  if (!startDate.isValid() || !endDate.isValid() || startDate.isAfter(endDate, 'day')) {
    return [];
  }

  const existingDates = new Set(existingDateKeys.filter(Boolean));
  const dates = [];
  let cursor = startDate;

  while (cursor.isBefore(endDate) || cursor.isSame(endDate, 'day')) {
    const dateKey = cursor.format('YYYY-MM-DD');
    if (!existingDates.has(dateKey)) {
      dates.push(dateKey);
    }
    cursor = cursor.add(1, 'day');
  }

  return dates;
};


const UsageDataSheet = () => {
  const { userData } = useSelector((state) => state.auth);
  const assignedAirports = useMemo(() => parseUserAirports(userData?.airports), [userData?.airports]);
  const isAdmin = userData?.role === 'admin';
  const initialFilters = useMemo(
    () => ({
      airport: assignedAirports.length === 1 ? assignedAirports[0] : '',
      statuses: [],
      year: '',
      month: '',
      start_date: '',
      end_date: '',
      focus_date: '',
      search: '',
      mismatch_only: false,
    }),
    [assignedAirports]
  );

  const [metadata, setMetadata] = useState(null);
  const [allAirports, setAllAirports] = useState([]);
  const [records, setRecords] = useState([]);
  const [suggestionRecords, setSuggestionRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queuedDraftRows, setQueuedDraftRows] = useState([]);
  const [draftDateKeys, setDraftDateKeys] = useState([]);
  const [isDownloadPdf, setIsDownloadPdf] = useState(false);
  const [filters, setFilters] = useState(initialFilters);

  const accessibleAirports = useMemo(() => {
    const allAirportCodes = allAirports.map((item) => item[0]).filter(Boolean);
    if (!assignedAirports.length || (isAdmin && !assignedAirports.length)) {
		return allAirportCodes.sort();
    }
    return allAirportCodes.filter((airport) => assignedAirports.includes(airport)).sort();
  }, [allAirports, assignedAirports, isAdmin]);

  const airportCityMap = useMemo(
    () =>
      Object.fromEntries(
        (allAirports || [])
          .map((item) => {
            const code = String(item?.[0] || '').trim().toUpperCase();
            const city = normalizeCityName(item?.[1]);
            if (!code) {
              return null;
            }
            return [code, city];
          })
          .filter(Boolean)
      ),
    [allAirports]
  );

  const yearOptions = useMemo(() => metadata?.Year || [], [metadata]);
  const monthOptions = useMemo(() => metadata?.MONTH_YEAR || [], [metadata]);
  const statusOptions = useMemo(() => metadata?.Status || [], [metadata]);

  const airlineNames = useMemo(() => {
    const activeAirlines = (metadata?.airlines || []).map((item) => item?.value).filter(Boolean);
    const recordAirlines = records.flatMap((record) => Object.keys(record.airlines || {}));
    return Array.from(new Set([...activeAirlines, ...recordAirlines]));
  }, [metadata, records]);

  const loadMetadata = useCallback(async () => {
    const [metaResponse, airportResponse] = await Promise.all([
      axiosGet(API_GET_DATA),
      axiosGet(API_ALLAIRPORT),
    ]);
    setMetadata(metaResponse?.data || null);
    setAllAirports(airportResponse?.data || []);
  }, []);

  const loadRecords = useCallback(async (nextFilters) => {
    setLoading(true);
    try {
      const params = {
        limit: 'all',
        airport: nextFilters.airport || '',
        status: (nextFilters.statuses || []).join(','),
        year: nextFilters.year || '',
        month: nextFilters.month || '',
        search: nextFilters.search || '',
        mismatch_only: nextFilters.mismatch_only ? 'true' : 'false',
      };

      if (nextFilters.focus_date) {
        params.start_date = nextFilters.focus_date;
        params.end_date = nextFilters.focus_date;
      } else {
        if (nextFilters.start_date) {
          params.start_date = nextFilters.start_date;
        }
        if (nextFilters.end_date) {
          params.end_date = nextFilters.end_date;
        }
      }

      const response = await axiosGet(API_USAGE_RECORDS, { params });
      setRecords(response?.data || []);
      if (!nextFilters.airport) {
        setDraftDateKeys([]);
      }
    } catch (error) {
      notification.error({
        message: 'Unable to Load Usage Records',
        description: 'Usage data sheet could not be loaded.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSuggestionRecords = useCallback(async (nextFilters) => {
    if (!nextFilters.airport) {
      setSuggestionRecords([]);
      return;
    }

    try {
      const params = {
        limit: 'all',
        airport: nextFilters.airport || '',
        year: nextFilters.year || '',
        month: nextFilters.month || '',
        search: '',
        mismatch_only: 'false',
      };

      const response = await axiosGet(API_USAGE_RECORDS, { params });
      setSuggestionRecords(response?.data || []);
    } catch (error) {
      setSuggestionRecords([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        await loadMetadata();
        await Promise.all([
          loadRecords(initialFilters),
          initialFilters.airport ? loadSuggestionRecords(initialFilters) : Promise.resolve(setSuggestionRecords([])),
        ]);
      } catch (error) {
        notification.error({
          message: 'Unable to Load Data Page',
          description: 'Metadata for the Data page could not be loaded.',
        });
      }
    };

    init();
  }, [initialFilters, loadMetadata, loadRecords, loadSuggestionRecords]);

  useEffect(() => {
    if (!filters.airport && accessibleAirports.length === 1) {
      const nextFilters = { ...filters, airport: accessibleAirports[0] };
      setFilters(nextFilters);
      loadRecords(nextFilters);
      loadSuggestionRecords(nextFilters);
    }
  }, [accessibleAirports, filters, loadRecords, loadSuggestionRecords]);

  const updateFilter = (key, value, options = {}) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    if (options.autoLoad !== false) {
      loadRecords(nextFilters);
      loadSuggestionRecords(nextFilters);
    }
  };

  const updateDateFilter = (key, value) => {
    const nextFilters = { ...filters, [key]: value };

    if (value) {
      if (key === 'focus_date') {
        nextFilters.start_date = '';
        nextFilters.end_date = '';
      } else {
        nextFilters.focus_date = '';
      }
    }

    setFilters(nextFilters);
    loadRecords(nextFilters);
    loadSuggestionRecords(nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = {
      ...initialFilters,
      airport: initialFilters.airport || '',
    };

    setQueuedDraftRows([]);
    setDraftDateKeys([]);
    setFilters(nextFilters);
    loadRecords(nextFilters);
    loadSuggestionRecords(nextFilters);
  };

  const tableRows = useMemo(
    () => records.map((record) => buildUsageSheetRow(record, airlineNames)),
    [airlineNames, records]
  );

  const visibleExportRows = useMemo(() => {
    return tableRows.map((row) => [
      row.airport_code,
      airportCityMap[String(row.airport_code || '').toUpperCase()] || '',
      formatDisplayDate(row.flight_date),
      row.total_dep_pax,
      calcDyDiff(row),
      calcAirlineSum(row, airlineNames),
      row.dy_pax,
      row.dy_pax_entry_app,
      row.dy_pax_entry_otdy_kiosks,
      row.dy_pax_pesc,
      row.dy_pax_boarding,
      ...airlineNames.map((airlineName) => row[airlineColumnKey(airlineName)] || 0),
      row.remarks || '',
    ]);
  }, [airlineNames, airportCityMap, tableRows]);

  const exportHeaders = useMemo(
    () => [
      'Airport',
      'CITY',
      'Date',
      'TOTAL DEP PAX',
      'DY DIFF',
      'AL SUM',
      'DYPAX',
      'ENTRY APP',
      'KIOSKS',
      'PESC',
      'BOARDING',
      ...airlineNames.map((airlineName) => airlineName.toUpperCase()),
      'REMARKS',
    ],
    [airlineNames]
  );

  const selectedMonthWindow = useMemo(
    () => resolveMonthWindow(filters.year, filters.month),
    [filters.month, filters.year]
  );

  const airportDateKeys = useMemo(() => {
    if (!filters.airport || !suggestionRecords.length) {
      return [];
    }

    return suggestionRecords
      .filter((record) => record.airport_code === filters.airport)
      .map((record) => String(record.flight_date || '').slice(0, 10))
      .filter(Boolean)
      .sort();
  }, [filters.airport, suggestionRecords]);

  const firstFilledDate = useMemo(
    () => (airportDateKeys.length ? airportDateKeys[0] : ''),
    [airportDateKeys]
  );

  const latestFilledDate = useMemo(
    () => (airportDateKeys.length ? airportDateKeys[airportDateKeys.length - 1] : ''),
    [airportDateKeys]
  );

  const continuationDates = useMemo(
    () => buildContinuationDates(latestFilledDate).filter((dateValue) => !draftDateKeys.includes(dateValue)),
    [draftDateKeys, latestFilledDate]
  );

  const activeRangeMissingDates = useMemo(() => {
    if (!filters.airport || !firstFilledDate) {
      return [];
    }

    return buildMissingDatesInRange(
      firstFilledDate,
      dayjs().format('YYYY-MM-DD'),
      [...airportDateKeys, ...draftDateKeys]
    );
  }, [airportDateKeys, draftDateKeys, filters.airport, firstFilledDate]);

  const missingDates = useMemo(() => {
    if (!filters.airport || !selectedMonthWindow) {
      return [];
    }

    return buildMissingDatesInRange(
      selectedMonthWindow.start.format('YYYY-MM-DD'),
      selectedMonthWindow.end.format('YYYY-MM-DD'),
      [...airportDateKeys, ...draftDateKeys]
    );
  }, [airportDateKeys, draftDateKeys, filters.airport, selectedMonthWindow]);

  const quickSuggestDates = selectedMonthWindow ? missingDates : activeRangeMissingDates;

  const explorerAnalytics = useMemo(() => {
    const uniqueAirports = Array.from(new Set(tableRows.map((row) => row.airport_code).filter(Boolean)));
    const uniqueDates = Array.from(new Set(tableRows.map((row) => row.flight_date).filter(Boolean))).sort();
    const mismatchRows = tableRows.filter(
      (row) => hasEntryMismatch(row) || hasAirlineMismatch(row, airlineNames) || row.has_mismatch
    );
    const negativeRows = tableRows.filter((row) => hasNegativeTouchpoint(row));
    const totalDepPax = tableRows.reduce((sum, row) => sum + Number(row.total_dep_pax || 0), 0);
    const totalDyPax = tableRows.reduce((sum, row) => sum + Number(row.dy_pax || 0), 0);
    const totalEntryPax = tableRows.reduce(
      (sum, row) => sum + Number(row.dy_pax_entry_app || 0) + Number(row.dy_pax_entry_otdy_kiosks || 0),
      0
    );
    const totalAirlinePax = tableRows.reduce((sum, row) => sum + calcAirlineSum(row, airlineNames), 0);
    const airlineTotals = airlineNames
      .map((airlineName) => ({
        name: airlineName,
        value: tableRows.reduce((sum, row) => sum + Number(row[airlineColumnKey(airlineName)] || 0), 0),
      }))
      .filter((item) => item.value > 0)
      .sort((left, right) => right.value - left.value);

    return {
      rowCount: tableRows.length,
      uniqueAirportCount: uniqueAirports.length,
      uniqueDateCount: uniqueDates.length,
      mismatchCount: mismatchRows.length,
      negativeCount: negativeRows.length,
      totalDepPax,
      totalDyPax,
      totalEntryPax,
      totalAirlinePax,
      dySharePercent: totalDepPax ? (totalDyPax * 100) / totalDepPax : 0,
      averageDyPerDay: uniqueDates.length ? totalDyPax / uniqueDates.length : 0,
      visibleStartDate: uniqueDates[0] || '',
      visibleEndDate: uniqueDates[uniqueDates.length - 1] || '',
      topAirlines: airlineTotals.slice(0, 5),
    };
  }, [airlineNames, tableRows]);

  const tablePagination = useMemo(
    () => ({
      defaultPageSize: 100,
      showSizeChanger: true,
      showLessItems: true,
      hideOnSinglePage: true,
      pageSizeOptions: ['100', '250', '500', '1000'],
      position: ['topRight', 'bottomRight'],
      size: 'large',
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${formatIndianStyle(total)}`,
    }),
    []
  );

  const explorerSummaryNote = useMemo(() => {
    const scopeLabel = filters.airport
      ? filters.airport
      : accessibleAirports.length !== allAirports?.length
        ? accessibleAirports.join(', ')
        : 'All accessible airports';
    const coverageLabel =
      explorerAnalytics.visibleStartDate && explorerAnalytics.visibleEndDate
        ? `${formatDisplayDate(explorerAnalytics.visibleStartDate)} to ${formatDisplayDate(explorerAnalytics.visibleEndDate)}`
        : 'No date coverage';

    return `Loaded ${formatIndianStyle(explorerAnalytics.rowCount)} rows for ${scopeLabel}. Coverage: ${coverageLabel}.`;
  }, [accessibleAirports, allAirports?.length, explorerAnalytics.rowCount, explorerAnalytics.visibleEndDate, explorerAnalytics.visibleStartDate, filters.airport]);

  const queueDraftRows = (rowsToAdd = []) => {
    if (!rowsToAdd.length) {
      return;
    }
    setQueuedDraftRows(rowsToAdd);
    setDraftDateKeys((current) =>
      Array.from(new Set([...current, ...rowsToAdd.map((row) => row.flight_date).filter(Boolean)]))
    );
  };

  const handleSaveRows = async (changedRows) => {
    const originalRowsById = Object.fromEntries(tableRows.map((row) => [row.id, row]));

    for (const row of changedRows) {
      if (!hasUsageRowChanged(row, originalRowsById[row.id], airlineNames)) {
        continue;
      }

      const payload = buildUsagePayload(row, airlineNames);
      if (row.id) {
        await axiosPut(API_UPDATE_USAGE_RECORD(row.id), payload);
      } else {
        await axiosPost(API_CREATE_USAGE_RECORD, payload);
      }
    }

    setQueuedDraftRows([]);
    await loadMetadata();
    await Promise.all([loadRecords(filters), loadSuggestionRecords(filters)]);
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) {
      return;
    }
    await axiosDelete(API_DELETE_USAGE_RECORD(row.id), {});
    await Promise.all([loadRecords(filters), loadSuggestionRecords(filters)]);
  };

  const downloadPageAsPdf = async () => {
    setIsDownloadPdf(true);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const element = document.getElementById('UsageDataSheetPanel');
    if (!element) {
      setIsDownloadPdf(false);
      return;
    }

    const contentWidth = Math.max(element.offsetWidth, element.scrollWidth);
    const contentHeight = Math.max(element.offsetHeight, element.scrollHeight);
    const timestamp = getTimestamp();

    try {
      await html2pdf()
        .from(element)
        .set({
          margin: 5,
          image: { type: 'jpeg', quality: 1 },
          pagebreak: { mode: ['css', 'legacy'] },
          html2canvas: {
            scale: 1.5,
            letterRendering: true,
            logging: false,
            useCORS: true,
            scrollX: 0,
            scrollY: 0,
            windowWidth: contentWidth,
            windowHeight: contentHeight,
          },
          jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' },
        })
        .save(`Data_${timestamp}.pdf`);
    } finally {
      setIsDownloadPdf(false);
    }
  };

  const exportToExcel = () => {
    const workbookRows = visibleExportRows.map((row) => [...row]);
    workbookRows.unshift(exportHeaders);
    exportRowsToExcel(
      workbookRows.map((row) =>
        Object.fromEntries(exportHeaders.map((header, index) => [header, row[index]]))
      ),
      'usage-data-sheet',
      'Data Explorer'
    );
  };

  const columns = useMemo(() => {
    const airportSelectOptions = accessibleAirports.map((airport) => ({ label: airport, value: airport }));

    return [
      {
        key: 'airport_code',
        title: 'Airport',
        type: 'select',
        required: true,
        options: airportSelectOptions,
        width: 120,
        fixed: isDownloadPdf ? undefined : 'left',
        renderReadonly: (value) => (
          <p className="text-center font-semibold text-white bg-[#3B82F6] px-2 rounded-sm" style={{ marginBottom: 0 }}>
            {value || ''}
          </p>
        ),
      },
      {
        key: 'city',
        title: 'CITY',
        editable: false,
        width: 160,
        fixed: isDownloadPdf ? undefined : 'left',
        renderReadonly: (_, row) => {
          const code = String(row.airport_code || '').trim().toUpperCase();
          return <div>{airportCityMap[code] || ''}</div>;
        },
      },
      {
        key: 'flight_date',
        title: 'Date',
        type: 'date',
        required: true,
        width: 130,
        fixed: isDownloadPdf ? undefined : 'left',
        renderReadonly: (value) => <div>{formatDisplayDate(value)}</div>,
      },
      {
        key: 'total_dep_pax',
        title: 'Total Dep PAX',
        type: 'number',
        required: true,
        min: 0,
        width: 150,
        renderReadonly: (value) => renderMetricPill(value),
      },
      {
        key: 'dy_diff',
        title: 'DY DIFF',
        editable: false,
        width: 120,
        renderReadonly: (_, row) =>
          renderMetricPill(calcDyDiff(row), {
            highlight: hasEntryMismatch(row) || row.has_mismatch,
          }),
      },
      {
        key: 'airline_sum',
        title: 'AL SUM',
        editable: false,
        width: 120,
        renderReadonly: (_, row) => {
          const airlineSum = calcAirlineSum(row, airlineNames);
          return renderMetricPill(airlineSum, {
            highlight: hasAirlineMismatch(row, airlineNames),
          });
        },
      },
      {
        key: 'dy_pax',
        title: 'DYPAX',
        type: 'number',
        width: 120,
        renderReadonly: (value, row) =>
          renderMetricPill(value, {
            highlight: hasEntryMismatch(row) || hasAirlineMismatch(row, airlineNames) || row.has_mismatch,
          }),
      },
      {
        key: 'dy_pax_entry_app',
        title: 'ENTRY APP',
        type: 'number',
        width: 130,
        renderReadonly: (value, row) =>
          renderMetricPill(value, {
            highlight: hasEntryMismatch(row) || row.has_mismatch,
          }),
      },
      {
        key: 'dy_pax_entry_otdy_kiosks',
        title: 'KIOSKS',
        type: 'number',
        width: 120,
        renderReadonly: (value, row) =>
          renderMetricPill(value, {
            highlight: hasEntryMismatch(row) || row.has_mismatch,
          }),
      },
      {
        key: 'dy_pax_pesc',
        title: 'PESC',
        type: 'number',
        width: 110,
        renderReadonly: (value) => renderMetricPill(value),
      },
      {
        key: 'dy_pax_boarding',
        title: 'BOARDING',
        type: 'number',
        width: 130,
        renderReadonly: (value) => renderMetricPill(value),
      },
      ...airlineNames.map((airlineName) => ({
        key: airlineColumnKey(airlineName),
        title: airlineName,
        type: 'number',
        min: 0,
        width: 120,
        renderReadonly: (value) => renderMetricPill(value),
      })),
      {
        key: 'remarks',
        title: 'Remarks',
        type: 'textarea',
        width: 220,
      },
    ];
  }, [accessibleAirports, airlineNames, airportCityMap, isDownloadPdf]);

  return (
    <div id="UsageDataSheetPanel" className="usage-data-sheet-panel">
      <div style={{ marginBottom: 0, position: 'relative' }}>
        <Title level={4} style={{ borderRadius: '5px 25px', padding: '5px 25px', marginTop: 20, textAlign: 'center' }}>
          Data Explorer
        </Title>

        {!isDownloadPdf ? (
          <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* <CustomButton
              className="ml-2"
              icon={
                <img
                  src={require('../../assests/download.png')}
                  alt="download"
                  loading="lazy"
                  style={{ width: 15, height: 15 }}
                />
              }
              label="PDF"
              onClick={downloadPageAsPdf}
            /> */}
            <CustomButton
              className="ml-2"
              icon={
                <img
                  src={require('../../assests/download.png')}
                  alt="download"
                  loading="lazy"
                  style={{ width: 15, height: 15 }}
                />
              }
              label="XLSX"
              onClick={exportToExcel}
            />
          </div>
        ) : null}
      </div>

      <MismatchGuideAccordion>
          <div>`DY DIFF` = `DYPAX - (ENTRY APP + KIOSKS)`.</div>
          <div>`AL SUM` = total of all airline columns for the row.</div>
          <div>Red summary cells mean the row still does not reconcile. Update `DYPAX`, entry values, or airline columns until the mismatch clears.</div>
          <div>Leave airport empty to inspect all accessible rows. Select one airport to run continuity checks, detect missing days, and insert missing dates directly.</div>
      </MismatchGuideAccordion>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <Space wrap size="middle">
          <Select
            placeholder="Select airport"
            style={{ minWidth: 200 }}
            value={filters.airport || undefined}
            options={accessibleAirports.map((airport) => ({ label: airport, value: airport }))}
            onChange={(value) => updateFilter('airport', value || '')}
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Filter status"
            style={{ minWidth: 220 }}
            value={filters.statuses}
            options={statusOptions}
            onChange={(value) => updateFilter('statuses', value || [])}
            allowClear
            maxTagCount="responsive"
          />
          <Select
            placeholder="Select year"
            style={{ minWidth: 140 }}
            value={filters.year || undefined}
            options={yearOptions}
            onChange={(value) => updateFilter('year', value || '')}
            allowClear
          />
          <Select
            placeholder="Select month"
            style={{ minWidth: 170 }}
            value={filters.month || undefined}
            options={monthOptions}
            onChange={(value) => updateFilter('month', value || '')}
            allowClear
          />
          <DatePicker
            placeholder="Start date"
            format="DD/MM/YYYY"
            style={{ width: 170 }}
            value={filters.start_date ? dayjs(filters.start_date) : null}
            onChange={(value) => updateDateFilter('start_date', value ? value.format('YYYY-MM-DD') : '')}
            disabledDate={(current) =>
              filters.end_date ? current && current.isAfter(dayjs(filters.end_date), 'day') : false
            }
          />
          <DatePicker
            placeholder="End date"
            format="DD/MM/YYYY"
            style={{ width: 170 }}
            value={filters.end_date ? dayjs(filters.end_date) : null}
            onChange={(value) => updateDateFilter('end_date', value ? value.format('YYYY-MM-DD') : '')}
            disabledDate={(current) =>
              filters.start_date ? current && current.isBefore(dayjs(filters.start_date), 'day') : false
            }
          />
          <DatePicker
            placeholder="Select date"
            format="DD/MM/YYYY"
            style={{ width: 170 }}
            value={filters.focus_date ? dayjs(filters.focus_date) : null}
            onChange={(value) => updateDateFilter('focus_date', value ? value.format('YYYY-MM-DD') : '')}
          />
          <Input
            placeholder="Search remarks"
            style={{ width: 220 }}
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            onPressEnter={() => loadRecords({ ...filters })}
          />
        </Space>

        <Space wrap>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong>Mismatches only</Text>
            <Switch checked={filters.mismatch_only} onChange={(checked) => updateFilter('mismatch_only', checked)} />
          </div>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadRecords(filters);
              loadSuggestionRecords(filters);
            }}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {!filters.airport ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20, width: "fit-content" }}
          message={isAdmin ? "Showing all accessible airports. Select one airport to run continuity checks and insert missing dates." : "Select one airport to run continuity checks and insert missing dates."}
        />
      ) : null}

      <div style={SUMMARY_GRID_STYLE}>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">Rows Loaded</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{formatIndianStyle(explorerAnalytics.rowCount)}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>{explorerSummaryNote}</div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">Airports</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{formatIndianStyle(explorerAnalytics.uniqueAirportCount)}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            {filters.airport ? 'Single-airport continuity mode active.' : 'Cross-airport explorer view.'}
          </div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">Dates</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{formatIndianStyle(explorerAnalytics.uniqueDateCount)}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            {explorerAnalytics.visibleStartDate && explorerAnalytics.visibleEndDate
              ? `${formatDisplayDate(explorerAnalytics.visibleStartDate)} to ${formatDisplayDate(explorerAnalytics.visibleEndDate)}`
              : 'No loaded date range'}
          </div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">{selectedMonthWindow ? 'Month Gaps' : 'Coverage Gaps'}</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>
            {formatIndianStyle(selectedMonthWindow ? missingDates.length : activeRangeMissingDates.length)}
          </div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            {filters.airport
              ? 'Missing dates detected in the active continuity range.'
              : 'Select one airport to enable continuity analytics.'}
          </div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">Mismatch Rows</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6, color: explorerAnalytics.mismatchCount ? '#D70040' : '#0f172a' }}>
            {formatIndianStyle(explorerAnalytics.mismatchCount)}
          </div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            Negative touchpoint rows: {formatIndianStyle(explorerAnalytics.negativeCount)}
          </div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">Total Dep PAX</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{formatMetric(explorerAnalytics.totalDepPax)}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            DY share: {formatPercentValue(explorerAnalytics.dySharePercent)}
          </div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">DY PAX</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{formatMetric(explorerAnalytics.totalDyPax)}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            Entry total: {formatMetric(explorerAnalytics.totalEntryPax)}
          </div>
        </div>
        <div style={SUMMARY_CARD_STYLE}>
          <Text type="secondary">Airline Total</Text>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{formatMetric(explorerAnalytics.totalAirlinePax)}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
            Avg DY/day: {formatMetric(explorerAnalytics.averageDyPerDay)}
          </div>
        </div>
      </div>

      <div style={ANALYTICS_PANEL_STYLE}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Text strong>Top Airline Totals</Text>
          {explorerAnalytics.topAirlines.length ? (
            explorerAnalytics.topAirlines.map((item) => (
              <Tag key={item.name} color="blue" style={{ paddingInline: 10, paddingBlock: 4 }}>
                {item.name}: {formatMetric(item.value)}
              </Tag>
            ))
          ) : (
            <Text type="secondary">No airline totals in the current view.</Text>
          )}
        </div>
      </div>

      {filters.airport ? (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            border: '1px solid #d9d9d9',
            borderRadius: 12,
            background: '#fafafa',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: quickSuggestDates.length ? 12 : 0,
            }}
          >
            <div>
              <Text strong>{selectedMonthWindow ? 'Missing date suggestions' : 'Continue from last filled date'}</Text>
              <div style={{ color: '#64748b', fontSize: 12 }}>
                {selectedMonthWindow
                  ? `Dates are checked for ${filters.airport} within the selected month window.`
                  : firstFilledDate
                    ? activeRangeMissingDates.length
                      ? `Dates are checked for ${filters.airport} from ${formatDisplayDate(firstFilledDate)} to today. Missing dates in that range are listed below.`
                      : latestFilledDate
                        ? `Dates are checked for ${filters.airport} from ${formatDisplayDate(firstFilledDate)} to today. No missing dates were found. Latest filled row is ${formatDisplayDate(latestFilledDate)}.`
                        : `Dates are checked for ${filters.airport} from ${formatDisplayDate(firstFilledDate)} to today. No missing dates were found.`
                    : `No rows found yet for ${filters.airport}. Start by adding the first date row.`}
              </div>
            </div>

            {/* {quickSuggestDates.length ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  queueDraftRows(
                    quickSuggestDates.slice(0, 10).map((dateValue) =>
                      buildDraftUsageRow({
                        airportCode: filters.airport,
                        flightDate: dateValue,
                        airlineNames,
                      })
                    )
                  )
                }
              >
                Insert First 10 Suggested Dates
              </Button>
            ) : null} */}
          </div>

          {quickSuggestDates.length ? (
            <Space wrap>
              {quickSuggestDates.slice(0, 20).map((dateValue) => (
                <Tag
                  key={dateValue}
                  color="green"
                  style={{ cursor: 'pointer', paddingInline: 10, paddingBlock: 4 }}
                //   onClick={() =>
                //     queueDraftRows([
                //       buildDraftUsageRow({
                //         airportCode: filters.airport,
                //         flightDate: dateValue,
                //         airlineNames,
                //       }),
                //     ])
                //   }
	                >
	                  <CalendarOutlined /> {formatDisplayDate(dateValue)}
	                </Tag>
	              ))}
            </Space>
          ) : (
            <Text type="secondary">
              {selectedMonthWindow
                ? 'No missing dates detected for the current airport and month selection.'
                : firstFilledDate
                  ? 'No missing dates detected between the first filled date and today.'
                  : 'No forward date suggestions are pending for this airport.'}
            </Text>
          )}
        </div>
      ) : null}

      <EditableGridTable
        data={tableRows}
        columns={columns}
        rowKey="id"
        canEdit={false}
        onSaveRows={handleSaveRows}
        onDeleteRow={handleDeleteRow}
        getNewRow={() =>
          buildDraftUsageRow({
            airportCode: filters.airport || accessibleAirports[0] || '',
            flightDate: filters.focus_date || quickSuggestDates[0] || continuationDates[0] || '',
            airlineNames,
          })
        }
        cloneRow={(row) => ({
          ...row,
          id: null,
          flight_date: '',
        })}
        searchKeys={['airport_code', 'flight_date', 'remarks']}
        searchPlaceholder="Search visible editor rows"
        note={explorerSummaryNote}
        showSearch={false}
        toolbarPosition="bottom"
        scroll={isDownloadPdf ? undefined : { x: 'max-content' }}
        pagination={isDownloadPdf ? false : tablePagination}
        loading={loading}
        tableLayout={isDownloadPdf ? 'auto' : 'fixed'}
        externalDraftRows={queuedDraftRows}
        onExternalDraftRowsConsumed={() => setQueuedDraftRows([])}
      />

      {loading ? <Text type="secondary">Refreshing usage records...</Text> : null}
    </div>
  );
};


export default UsageDataSheet;
